import React, { useCallback, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import ChargesRow from "../../billing/ChargesRow";
import OneTimeChargesModal from "./OneTimeChargesModal";
import { UPDATE_SHIPMENT_MUTATION } from "../../../constants/graphql/mutations";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import useAuth from "../../../hooks/useAuth";
import { getShipmentValues } from "../../../utils/shipment";
import { getInboundBillingType } from "../../../utils/billingHelpers";
import CategoryChargesInfoModal from "../../billing/CategoryChargesInfoModal";
import CategoryChargesEditModal from "../../billing/CategoryChargesEditModal";
import InboundChargesHeader from "./InboundChargesHeader";
import BillingQuantitiesModal from "../../capture/ReceivingWizard/Shipment/Billing/BillingQuantitiesModal";

const chargeTitles = {
  inboundCharges: "Inbound Charges",
  laborCharges: "Labor Charges",
  materialCharges: "Material Charges",
  equipmentCharges: "Equipment Charges",
  openInspectCharges: "Open & Inspect Charges",
};

export default function InboundChargesSummaryModal({ shipment, project, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isInternalStaff = ["admin", "manager", "inventory"].includes(user?.role);
  const queryParams = new URLSearchParams(location.search);
  const billing = shipment?.billingCharges || {};
  const [openOneTime, setOpenOneTime] = useState(false);
  const openChargesInfoModal = queryParams.get("openChargesInfoModal");

  const toggleChargesInfoModal = useCallback(
    value => {
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set("openChargesInfoModal", value);
      } else {
        params.delete("openChargesInfoModal");
      }
      navigate({ search: params.toString() });
    },
    [navigate, location.search]
  );

  const [openChargesEditModal, setOpenChargesEditModal] = useState(null);
  const [openBillingQuantitiesModal, setOpenBillingQuantitiesModal] = useState(false);
  const [updateShipment, { loading }] = useMutation(UPDATE_SHIPMENT_MUTATION);
  const { notifySuccess, notifyError } = useNotification();

  const handleSubmit = useCallback(
    async values => {
      try {
        await updateShipment({
          variables: {
            id: shipment.id,
            input: {
              ...getShipmentValues(shipment),
              oneTimeCharges: {
                list: values.list,
              },
            },
          },
        });
        notifySuccess("One-time charges saved");
        setOpenOneTime(false);
      } catch (err) {
        notifyError("Failed to save charges");
      }
    },
    [shipment, updateShipment, notifySuccess, notifyError]
  );

  const getViewShipmentTo = () => {
    const returnTo = `${location.pathname}${location.search}`;
    const params = new URLSearchParams(location.search);
    params.delete("inboundChargesSummaryId");
    params.set("step", "shipment");
    params.set("receiving", "true");
    params.set("editShipment", "true");
    params.set("shipmentId", shipment.shipmentId);
    params.set("id", shipment.id);
    params.set("returnTo", returnTo);
    return { pathname: location.pathname, search: params.toString() };
  };

  const charges = {
    inboundCharges: billing.inbound_total || 0,
    laborCharges: billing.labor_total || 0,
    materialCharges: billing.material_total || 0,
    equipmentCharges: billing.equipment_total || 0,
    openInspectCharges: billing.open_inspect_total || 0,
    oneTimeCharges: billing.one_time_total || 0,
    salesTax: shipment?.salesTax?.total || 0,
    billingCharges: billing.grand_total || 0,
  };

  const inboundBillingType = shipment?.billingType || getInboundBillingType(project);

  const hasWeightMismatch =
    Number(shipment?.totalWeight || 0) > 0 &&
    Number(shipment?.calculatedTotalWeight || 0) > 0 &&
    Math.abs(Number(shipment.totalWeight) - Number(shipment.calculatedTotalWeight)) > 1;

  const formatWeight = value =>
    `${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} lbs`;

  const handleOpenBillingQuantities = useCallback(() => {
    if (!inboundBillingType) {
      notifyError("No billing type selected for this shipment");
      return;
    }
    setOpenBillingQuantitiesModal(true);
  }, [inboundBillingType, notifyError]);

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth={false}
        sx={{ "& .MuiDialog-paper": { maxWidth: "440px", width: "100%" } }}
        aria-labelledby="inbound-charges-summary-title"
      >
        <DialogTitle id="inbound-charges-summary-title">
          <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
            Inbound Charges Summary
          </Typography>
          <IconButton
            onClick={onClose}
            size="large"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <InboundChargesHeader shipment={shipment} />
          {hasWeightMismatch && (
            <Alert severity="warning">
              Entered total weight: {formatWeight(shipment.totalWeight)}. Calculated item weight:{" "}
              {formatWeight(shipment.calculatedTotalWeight)}.
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={1} mx="auto" mt={3}>
            <ChargesRow
              value={charges.inboundCharges}
              label="Inbound Charges"
              operator="plus"
              onInfoClick={() => toggleChargesInfoModal("inboundCharges")}
            />
            <ChargesRow
              value={charges.laborCharges}
              label="Labor Charges"
              operator="plus"
              onInfoClick={() => toggleChargesInfoModal("laborCharges")}
            />
            <ChargesRow
              value={charges.materialCharges}
              label="Material Charges"
              operator="plus"
              onInfoClick={() => toggleChargesInfoModal("materialCharges")}
            />
            <ChargesRow
              value={charges.equipmentCharges}
              label="Equipment Charges"
              operator="plus"
              onInfoClick={() => toggleChargesInfoModal("equipmentCharges")}
            />
            <ChargesRow
              value={charges.openInspectCharges}
              label="Open & Inspect Charges"
              operator="plus"
              onInfoClick={() => toggleChargesInfoModal("openInspectCharges")}
            />
            <ChargesRow
              value={charges.oneTimeCharges}
              label="One-Time Flat Charges"
              operator="plus"
              onInfoClick={() => setOpenOneTime(true)}
            />
            <ChargesRow
              value={charges.salesTax}
              label="Sales Tax"
              operator="plus"
              showInfoButton={false}
            />
            <Divider />
            <ChargesRow
              value={charges.billingCharges}
              label="Grand Total"
              operator="equals"
              showInfoButton={false}
            />
          </Box>
          {isInternalStaff && (
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button variant="outlined" color="primary" component={Link} to={getViewShipmentTo()}>
                View Receiving Details
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      {openChargesInfoModal && (
        <CategoryChargesInfoModal
          onClose={() => toggleChargesInfoModal(null)}
          title={chargeTitles[openChargesInfoModal]}
          charges={shipment?.[openChargesInfoModal]}
          project={project}
          entity={shipment}
          categoryKey={openChargesInfoModal}
          onEdit={() => {
            if (openChargesInfoModal === "inboundCharges") {
              toggleChargesInfoModal(null);
              handleOpenBillingQuantities();
              return;
            }

            setOpenChargesEditModal(openChargesInfoModal);
            toggleChargesInfoModal(null);
          }}
        />
      )}
      {openChargesEditModal && (
        <CategoryChargesEditModal
          open={true}
          onClose={() => {
            toggleChargesInfoModal(openChargesEditModal);
            setOpenChargesEditModal(null);
          }}
          title={chargeTitles[openChargesEditModal]}
          charges={shipment?.[openChargesEditModal]}
          project={project}
          shipment={shipment}
          defaultOpenRatePicker={
            Object.entries(shipment?.[openChargesEditModal] || {}).length === 0
          }
          categoryKey={openChargesEditModal}
        />
      )}
      {openOneTime && (
        <OneTimeChargesModal
          shipment={shipment}
          onClose={() => setOpenOneTime(false)}
          onSubmit={handleSubmit}
          loading={loading}
        />
      )}
      {openBillingQuantitiesModal && inboundBillingType && (
        <BillingQuantitiesModal
          type="Inbound"
          billingType={inboundBillingType}
          shipment={shipment}
          palletTypes={project?.palletTypes}
          unitTypes={project?.unitTypes}
          onSave={() => {
            setOpenBillingQuantitiesModal(false);
            toggleChargesInfoModal("inboundCharges");
          }}
          allowClear
          onClose={() => setOpenBillingQuantitiesModal(false)}
        />
      )}
    </>
  );
}
